import { useEffect, useId, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { ChevronRight, ChevronsUpDown } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui'
import { CompanyBrand } from '@/components/CompanyBrand'
import { KaproLogo, KaproMark } from '@/components/Logo'
import { AccountMenuContent, UserAvatar } from './AccountMenu'
import {
  ADMIN_SECTION,
  isActivePath,
  isAdminPageActive,
  type AdminPage,
  type NavItem,
  type VisibleNavigation,
} from './navigation'

/*
 * One row of the rail, sized by pointer.
 *
 * shadcn's own size is 32px, which is right for a mouse and is what a fine
 * pointer now gets. On touch the row is 44px — the rail is also the phone's
 * menu, opened from the header as a sheet, and that is held with a gloved
 * thumb like everything else.
 *
 * The collapsed sizing carries `!` in shadcn's own variants, so matching it
 * here needs `!` too. Collapsed, the icon is centred in a square that is
 * centred in the rail: `justify-center` alone centres the icon within the
 * button and says nothing about where the button sits, and the rail's group
 * padding left it 2px left of the mark above and the avatar below.
 */
const ROW = cn(
  'h-11 gap-3 rounded-lg px-3 pointer-fine:h-8 pointer-fine:gap-2.5 pointer-fine:px-2',
  'group-data-[collapsible=icon]:size-11! pointer-fine:group-data-[collapsible=icon]:size-8!',
  'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0',
  'group-data-[collapsible=icon]:px-0! group-data-[collapsible=icon]:mx-auto',
  /*
   * The current page: a brand tint with brand ink, not shadcn's grey.
   *
   * It used to be a solid blue pill, which on a rail of six icons was the one
   * thing that could not be missed. With headings and a dozen rows that pill
   * became the loudest thing on the screen, so it is a tint now — still in the
   * brand colour, still with the icon coloured, so the current row reads at a
   * glance without shouting over the page.
   */
  'data-active:bg-primary/10 data-active:text-primary data-active:font-medium',
  'data-active:hover:bg-primary/15 data-active:hover:text-primary dark:data-active:bg-primary/20',
  '[&_svg]:size-5 pointer-fine:[&_svg]:size-4',
)

function NavRow({ item, pathname }: { item: NavItem; pathname: string }) {
  const t = useT()
  const Icon = item.icon

  return (
    <SidebarMenuItem>
      {/* `render`, not Radix's `asChild` — this shadcn build sits on Base UI,
          which composes through a render prop. */}
      <SidebarMenuButton
        render={<NavLink to={item.to} end={item.to === '/'} />}
        isActive={isActivePath(item.to, pathname)}
        // Shown only while the rail is collapsed, where the label is the
        // missing information.
        tooltip={t(item.labelKey)}
        className={ROW}
      >
        <Icon aria-hidden />
        <span className="group-data-[collapsible=icon]:hidden">{t(item.labelKey)}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/**
 * Administracija, opening onto its pages in the rail itself.
 *
 * This replaces the row of nine tabs that sat under the page title on every
 * admin screen. In the rail the pages are where every other destination is,
 * they cost the content area nothing, and the section is one click from any
 * screen rather than only from inside it.
 *
 * Open whenever an admin page is on screen, so the current page is visible
 * in the list; it can be folded away and stays folded until the next visit.
 *
 * Collapsed to icons there is no room for a list, so the same pages open as a
 * flyout beside the icon instead.
 */
function AdminSection({ pages, pathname }: { pages: AdminPage[]; pathname: string }) {
  const t = useT()
  const navigate = useNavigate()
  const { state, isMobile } = useSidebar()
  const listId = useId()

  const onAdmin = isActivePath(ADMIN_SECTION.to, pathname)
  const [open, setOpen] = useState(onAdmin)

  // Arriving in the section opens it; moving between its pages does not
  // reopen a list somebody has just folded.
  useEffect(() => {
    if (onAdmin) setOpen(true)
  }, [onAdmin])

  const Icon = ADMIN_SECTION.icon
  const label = t(ADMIN_SECTION.labelKey)

  if (state === 'collapsed' && !isMobile) {
    return (
      <SidebarMenuItem>
        {/*
          No `tooltip` here, on purpose: SidebarMenuButton implements it by
          wrapping itself in a TooltipTrigger's `render`, and this element is
          already a DropdownMenuTrigger's `render`. Two libraries composing onto
          one node is how you get a button that answers to neither. The flyout
          is headed with the section's name, which is what a tooltip would say.
        */}
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<SidebarMenuButton isActive={onAdmin} aria-label={label} className={ROW} />}
          >
            <Icon aria-hidden />
          </DropdownMenuTrigger>

          <DropdownMenuContent side="right" align="start" sideOffset={8} className="w-56">
            <DropdownMenuGroup>
              <DropdownMenuLabel>{label}</DropdownMenuLabel>
              {pages.map((page) => {
                const PageIcon = page.icon

                return (
                  <DropdownMenuItem
                    key={page.to}
                    onClick={() => navigate(page.to)}
                    className={cn(isAdminPageActive(page, pathname) && 'text-primary font-medium')}
                  >
                    <PageIcon aria-hidden />
                    {t(page.labelKey)}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={listId}
        // Folded away on an admin page, the section row is what says where
        // you are; open, the page's own row below does.
        isActive={onAdmin && !open}
        className={cn(ROW, onAdmin && open && 'text-primary')}
      >
        <Icon aria-hidden />
        <span className="group-data-[collapsible=icon]:hidden">{label}</span>
        <ChevronRight
          className={cn(
            'ml-auto size-4! opacity-60 transition-transform duration-200 motion-reduce:transition-none',
            open && 'rotate-90',
          )}
          aria-hidden
        />
      </SidebarMenuButton>

      {open && (
        <SidebarMenuSub id={listId} className="mt-0.5 gap-0.5 pointer-fine:gap-0">
          {pages.map((page) => (
            <SidebarMenuSubItem key={page.to}>
              <SidebarMenuSubButton
                render={<NavLink to={page.to} end={page.end} />}
                isActive={isAdminPageActive(page, pathname)}
                className={cn(
                  'h-10 pointer-fine:h-7',
                  'data-active:bg-primary/10 data-active:text-primary data-active:font-medium dark:data-active:bg-primary/20',
                )}
              >
                <span>{t(page.labelKey)}</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  )
}

/**
 * The account row at the foot of the rail.
 *
 * It opens a menu; it does not log you out. It used to *be* the logout button
 * — avatar, name and role all inside it — so the only thing identifying you on
 * screen was also the control that ended your session.
 */
function AccountRow() {
  const t = useT()
  const { user } = useAuth()
  const { isMobile } = useSidebar()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                className={cn(
                  ROW,
                  'h-12 pointer-fine:h-10',
                  'pointer-fine:group-data-[collapsible=icon]:size-8!',
                )}
                aria-label={t('account.ariaLabel', { name: user?.name ?? '' })}
              />
            }
          >
            <UserAvatar className="pointer-fine:size-6" />
            {/* Collapsed, the avatar alone stands for the account — the name and
                the chevron would only push it off centre. */}
            <span className="flex min-w-0 flex-1 flex-col text-left leading-tight group-data-[collapsible=icon]:hidden">
              <span className="truncate text-sm font-medium">{user?.name}</span>
              <span className="muted truncate text-xs">{user?.roleLabel}</span>
            </span>
            <ChevronsUpDown
              className="size-4! shrink-0 opacity-60 group-data-[collapsible=icon]:hidden"
              aria-hidden
            />
          </DropdownMenuTrigger>

          {/* Beside the rail on a desktop — a menu opening upward from the
              bottom edge is clipped when the rail is collapsed. In the phone's
              sheet there is no room beside it, so it opens upward there. */}
          <AccountMenuContent side={isMobile ? 'top' : 'right'} align={isMobile ? 'start' : 'end'} />
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

export function AppSidebar({ nav }: { nav: VisibleNavigation }) {
  const t = useT()
  const { pathname } = useLocation()
  const { isMobile, setOpenMobile } = useSidebar()

  // On a phone the rail is a sheet over the page; choosing a page is the end
  // of the errand, so the sheet gets out of the way.
  useEffect(() => {
    if (isMobile) setOpenMobile(false)
  }, [pathname, isMobile, setOpenMobile])

  return (
    /*
     * The glass is applied to the inner panel through an arbitrary variant:
     * `className` on <Sidebar> lands on the outer container, and the element
     * that actually paints the background is `[data-slot=sidebar-inner]` one
     * level down.
     */
    <Sidebar
      collapsible="icon"
      mobileLabel={t('nav.mainLabel')}
      className={cn(
        '[&_[data-slot=sidebar-inner]]:bg-sidebar/70',
        '[&_[data-slot=sidebar-inner]]:supports-[backdrop-filter]:bg-sidebar/55',
        '[&_[data-slot=sidebar-inner]]:backdrop-blur-2xl',
        '[&_[data-slot=sidebar-inner]]:backdrop-saturate-150',
      )}
    >
      <SidebarHeader className="h-(--shell-header) justify-center py-0">
        {/* justify-center only when collapsed. With the full logo hidden, the
            lone mark would otherwise sit at the left edge of the rail instead
            of over the icons below it. */}
        <div className="flex items-center px-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <KaproLogo size="sm" className="group-data-[collapsible=icon]:hidden" />
          <KaproMark className="hidden size-7 shrink-0 group-data-[collapsible=icon]:block" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {nav.groups.map((group) => (
          <SidebarGroup key={group.id} className="py-1">
            <SidebarGroupLabel className="h-7 pointer-fine:h-6">{t(group.labelKey)}</SidebarGroupLabel>
            <SidebarMenu className="gap-0.5">
              {group.items.map((item) => (
                <NavRow key={item.to} item={item} pathname={pathname} />
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}

        {nav.admin.length > 0 && (
          <SidebarGroup className="py-1">
            <SidebarMenu className="gap-0.5">
              <AdminSection pages={nav.admin} pathname={pathname} />
            </SidebarMenu>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="gap-1">
        <SidebarMenu className="gap-0.5">
          {nav.footer.map((item) => (
            <NavRow key={item.to} item={item} pathname={pathname} />
          ))}
        </SidebarMenu>

        {/*
          Whose factory this is, at the foot of the column next to the person
          signed in — the software above, the company it is running for below.
          The mark alone: most logos *are* a wordmark, so printing the name
          beside it said everything twice. Hidden when the rail collapses: a
          cropped logo is worse than none.
        */}
        <div className="border-[var(--border-subtle)] mx-2 border-t pt-2 empty:hidden group-data-[collapsible=icon]:hidden">
          <CompanyBrand className="justify-center px-1" showName={false} />
        </div>

        <AccountRow />
      </SidebarFooter>
    </Sidebar>
  )
}
